/*! JD - EMPIRE Theme | Copyright (c) 2026 JDA Commerce. All rights reserved. */

/**
 * Size normalisation — region aware.
 *
 * Shopify option values are typed by hand, so one catalogue ends up with all of
 * these for the same set of sizes:
 *
 *   S, M, L, XL, 2XL              S(4–6), M(8-10), L(12-14)
 *   S, M (Almost sold out), L     Small, Medium, Large
 *   4, 6, 8, 10  (US numeric)     36, 38, 40  (EU numeric)
 *   8, 10, 12    (UK numeric)     6, 7.5, 8   (US shoes)
 *   38, 39, 40   (EU shoes)       4 US | 35 EU,  EU 34 / US 4,  US WOMEN 8/8.5
 *
 * Everything is converted to ONE canonical number so the finder works no matter
 * which market the store sells in:
 *
 *   apparel  canonical = US women's numeric  (…0, 2, 4, 6, 8…)
 *   shoes    canonical = US women's numeric  (…6, 6.5, 7, 7.5…)
 *
 * The original label is always carried along — that is the string that has to be
 * clicked to select the variant, so it must never be lost in translation.
 */
(() => {
  const LADDER = ['2XS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'];

  /* ------------------------------------------------------------------ *
   * Conversion tables (women's).                                        *
   * EU shoe sizing is not linear against US, so it is a lookup, not a   *
   * formula. Apparel genuinely is linear, so those stay as offsets.     *
   * ------------------------------------------------------------------ */
  const SHOE_TABLE = [
    // us,   uk,   eu,   au
    [4.0, 2.0, 34.5, 2.0],
    [4.5, 2.5, 35.0, 2.5],
    [5.0, 3.0, 35.5, 3.0],
    [5.5, 3.5, 36.0, 3.5],
    [6.0, 4.0, 36.5, 4.0],
    [6.5, 4.5, 37.0, 4.5],
    [7.0, 5.0, 37.5, 5.0],
    [7.5, 5.5, 38.0, 5.5],
    [8.0, 6.0, 38.5, 6.0],
    [8.5, 6.5, 39.0, 6.5],
    [9.0, 7.0, 39.5, 7.0],
    [9.5, 7.5, 40.5, 7.5],
    [10.0, 8.0, 41.0, 8.0],
    [10.5, 8.5, 41.5, 8.5],
    [11.0, 9.0, 42.0, 9.0],
    [11.5, 9.5, 42.5, 9.5],
    [12.0, 10.0, 43.0, 10.0]
  ];

  // Apparel offsets from the US numeric canonical.
  const APPAREL_OFFSET = { us: 0, uk: 4, au: 4, eu: 32, fr: 30, it: 34 };

  // Alpha sizes cover a range of numeric sizes; the midpoint is what we store.
  const ALPHA_TO_US = {
    '2XS': 0, XS: 2, S: 4, M: 8, L: 12, XL: 16, '2XL': 20, '3XL': 24, '4XL': 28, '5XL': 32, '6XL': 36
  };
  const usToAlpha = (us) => {
    let best = null;
    let bestDist = Infinity;
    Object.keys(ALPHA_TO_US).forEach((k) => {
      const d = Math.abs(ALPHA_TO_US[k] - us);
      if (d < bestDist) { bestDist = d; best = k; }
    });
    return best;
  };

  const SYSTEMS = ['us', 'uk', 'eu', 'au'];

  const APPAREL_ALIASES = {
    XXS: '2XS', '2XS': '2XS',
    XS: 'XS', 'EXTRA SMALL': 'XS', 'X-SMALL': 'XS', XSMALL: 'XS',
    S: 'S', SMALL: 'S',
    M: 'M', MEDIUM: 'M', MED: 'M',
    L: 'L', LARGE: 'L',
    XL: 'XL', XLARGE: 'XL', 'X-LARGE': 'XL', 'X LARGE': 'XL', 'EXTRA LARGE': 'XL',
    XXL: '2XL', '2XL': '2XL', 'XX-LARGE': '2XL', XXLARGE: '2XL',
    XXXL: '3XL', '3XL': '3XL',
    XXXXL: '4XL', '4XL': '4XL',
    XXXXXL: '5XL', '5XL': '5XL',
    '6XL': '6XL'
  };

  // "M (Almost sold out)" and "S(4–6)" both carry their real value in front of
  // the bracket, so anything bracketed is noise for classification purposes.
  const stripNoise = (value) =>
    String(value)
      .replace(/\([^)]*\)/g, ' ')
      .replace(/\[[^\]]*\]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const normaliseApparel = (value) => {
    let s = stripNoise(value).toUpperCase().replace(/\./g, '');
    s = s.replace(/^SIZE\s+/, '').replace(/\s*-\s*/g, '-').trim();
    if (APPAREL_ALIASES[s]) return APPAREL_ALIASES[s];
    if (s.includes('/')) {
      const first = s.split('/')[0].trim();
      if (APPAREL_ALIASES[first]) return APPAREL_ALIASES[first];
    }
    return null;
  };

  /* ------------------------------------------------------------------ *
   * Explicit system tags inside the label win over any guessing.        *
   * "4 US | 35 EU" must give US 4, never EU 35.                         *
   * ------------------------------------------------------------------ */
  const taggedNumber = (raw, tag) => {
    const s = String(raw).toUpperCase();
    const before = s.match(new RegExp('(\\d{1,2}(?:[.,]\\d)?)\\s*' + tag + '\\b'));
    if (before) return parseFloat(before[1].replace(',', '.'));
    const after = s.match(new RegExp('\\b' + tag + '[^0-9]{0,10}(\\d{1,2}(?:[.,]\\d)?)'));
    if (after) return parseFloat(after[1].replace(',', '.'));
    return null;
  };

  const bareNumbers = (raw) =>
    (String(raw).match(/\d{1,2}(?:[.,]\d)?/g) || []).map((n) => parseFloat(n.replace(',', '.')));

  /**
   * Work out which system a whole set of option values is written in.
   * Done on the SET, not per value, because a single "38" is ambiguous while
   * "36, 37, 38, 39, 40" is clearly EU shoes.
   */
  const detectSystem = (values, kind) => {
    const joined = values.join(' ').toUpperCase();
    for (const sys of SYSTEMS) {
      if (values.some((v) => taggedNumber(v, sys.toUpperCase()) !== null)) return sys;
    }
    if (/\bFR\b/.test(joined)) return 'fr';
    if (/\bIT\b/.test(joined)) return 'it';

    const nums = values.flatMap(bareNumbers).filter((n) => !Number.isNaN(n));
    if (!nums.length) return null;
    const max = Math.max(...nums);
    const min = Math.min(...nums);

    if (kind === 'shoes') {
      if (min >= 33 && max <= 46) return 'eu';
      if (max <= 12 && min >= 1) return 'us';   // UK and US overlap; US is the safer default
      return 'us';
    }
    // apparel
    if (min >= 30 && max <= 56) return 'eu';
    if (max <= 20 && min >= 0) return 'us';
    return 'us';
  };

  /* ---------------------- conversion ---------------------- */
  const shoeToCanonical = (n, sys) => {
    if (sys === 'us') return n;
    const col = { uk: 1, eu: 2, au: 3 }[sys];
    if (col === undefined) return n;
    let best = null; let bestDist = Infinity;
    SHOE_TABLE.forEach((row) => {
      const d = Math.abs(row[col] - n);
      if (d < bestDist) { bestDist = d; best = row[0]; }
    });
    return bestDist <= 1.0 ? best : n;
  };

  const shoeFromCanonical = (us, sys) => {
    if (sys === 'us') return us;
    const col = { uk: 1, eu: 2, au: 3 }[sys];
    if (col === undefined) return us;
    let best = null; let bestDist = Infinity;
    SHOE_TABLE.forEach((row) => {
      const d = Math.abs(row[0] - us);
      if (d < bestDist) { bestDist = d; best = row[col]; }
    });
    return best === null ? us : best;
  };

  const apparelToCanonical = (n, sys) => n - (APPAREL_OFFSET[sys] || 0);
  const apparelFromCanonical = (us, sys) => us + (APPAREL_OFFSET[sys] || 0);

  /**
   * Shoes or apparel, from numbers alone.
   *
   * The ranges overlap badly — EU shoes are 34-43 and EU dresses are 36-44, US
   * shoes are 4-12 and US dresses are 0-20 — so the RANGE cannot decide it.
   * The STEP can: dress sizes go up in twos (36, 38, 40), shoe sizes go up in
   * ones or halves (38, 39, 40 / 7, 7.5, 8). Half sizes are shoes, always.
   */
  const guessKind = (values) => {
    const nums = [...new Set(values.flatMap(bareNumbers))].sort((a, b) => a - b);
    if (nums.some((n) => n % 1 !== 0)) return 'shoes';
    if (nums.length < 2) return nums[0] >= 33 ? 'shoes' : 'apparel';

    const steps = [];
    for (let i = 1; i < nums.length; i += 1) steps.push(nums[i] - nums[i - 1]);
    steps.sort((a, b) => a - b);
    const medianStep = steps[Math.floor(steps.length / 2)];

    if (medianStep >= 2) return 'apparel';
    if (medianStep <= 1) return 'shoes';
    return Math.max(...nums) >= 33 ? 'shoes' : 'apparel';
  };

  /**
   * Classify a whole option set.
   *
   * Returns { kind, system, sizes } where every size carries:
   *   original  — the exact label to click
   *   canonical — US-equivalent number for comparison
   *   key/rank  — alpha ladder position, for apparel
   */
  const classify = (values, minMatches = 3, forceSystem = null) => {
    const apparelAlpha = [];
    const numeric = [];

    values.forEach((original) => {
      const a = normaliseApparel(original);
      if (a) {
        apparelAlpha.push({ original, key: a, rank: LADDER.indexOf(a), canonical: ALPHA_TO_US[a] });
      }
    });

    // Alpha sizing is unambiguous — if enough values match, we are done.
    if (apparelAlpha.length >= minMatches) {
      apparelAlpha.sort((x, y) => x.rank - y.rank);
      return { kind: 'apparel', system: 'alpha', sizes: apparelAlpha };
    }

    // Otherwise treat it as numeric and work out the system from the whole set.
    const looksNumeric = values.filter((v) => bareNumbers(v).length > 0);
    if (looksNumeric.length < minMatches) return { kind: 'none', system: null, sizes: [] };

    const finalKind = guessKind(looksNumeric);
    /* US and UK numeric ranges are genuinely identical — "8, 10, 12" is a valid
       US dress run AND a valid UK dress run, and "4, 5, 6" is valid for both in
       shoes. No amount of inspection can separate them, so when the merchant has
       declared the system in the theme editor, that declaration wins outright. */
    const system = forceSystem && forceSystem !== 'auto' ? forceSystem : detectSystem(values, finalKind);

    looksNumeric.forEach((original) => {
      const tagged = SYSTEMS.map((s) => [s, taggedNumber(original, s.toUpperCase())]).find((x) => x[1] !== null);
      const sys = tagged ? tagged[0] : system;
      const raw = tagged ? tagged[1] : bareNumbers(original)[0];
      if (raw === undefined || Number.isNaN(raw)) return;
      const canonical = finalKind === 'shoes' ? shoeToCanonical(raw, sys) : apparelToCanonical(raw, sys);
      numeric.push({ original, raw, canonical, value: canonical, key: String(canonical) });
    });

    if (numeric.length < minMatches) return { kind: 'none', system: null, sizes: [] };
    numeric.sort((x, y) => x.canonical - y.canonical);
    return { kind: finalKind, system, sizes: numeric };
  };


  /* ---------------------- units ---------------------- */
  const lbToKg = (lb) => lb * 0.45359237;
  const kgToLb = (kg) => kg / 0.45359237;
  const inToCm = (i) => i * 2.54;
  const cmToIn = (c) => c / 2.54;

  window.JDSize = {
    LADDER,
    SYSTEMS,
    SHOE_TABLE,
    ALPHA_TO_US,
    normaliseApparel,
    classify,
    detectSystem,
    stripNoise,
    usToAlpha,
    shoeToCanonical,
    shoeFromCanonical,
    apparelToCanonical,
    apparelFromCanonical,
    lbToKg,
    kgToLb,
    inToCm,
    cmToIn,
    // kept for backwards compatibility with the previous US-only build
    normaliseShoe: (v) => {
      const n = taggedNumber(v, 'US');
      if (n !== null) return n;
      const b = bareNumbers(v).find((x) => x >= 3 && x <= 17);
      return b === undefined ? null : b;
    }
  };
})();
